import React, { useCallback, useEffect, useState } from 'react';
import { Guide } from 'gatorcomponents';
import { useDispatch, useSelector } from 'react-redux';
import useLocale from '@/hooks/useLocale';
import { AiChatPopover } from './AiChatPopover';
import { useResize } from '@/hooks/useResize';
import { commonActions } from '@/redux/modules';
import { getCookie, setCookie } from '@/utils/Application/cookies';

const AiGuides = () => {
  const dispatch = useDispatch();
  const windowRect = useResize();

  const { width } = windowRect;
  const isMobile = width < 960;
  const { aichat: aichatLocale } = useLocale();
  const { homeGuideLocale } = aichatLocale;
  const { aiChatWrapRef } = useSelector(state => state.home);
  const [steps, setSteps] = useState([]);
  const [usedRef, setUsedRef] = useState(null);
  const { firstname } = useSelector(state => state.summary);
  const { renderAiChatGuide } = useSelector(state => state.common);
  const hasViewAlready = getCookie('viewAiChatGuide');

  const handleClose = useCallback(() => {
    setSteps([]);
    dispatch(commonActions.setShouldRenderAiGuide(false));
    setCookie('viewAiChatGuide', false, -1);
  }, [
    dispatch,
  ]);

  const PopoverContent = useCallback(() => (
    <AiChatPopover
      homeGuideLocale={homeGuideLocale}
      name={firstname}
      handleClose={handleClose}
    />
  ), [homeGuideLocale, handleClose, firstname]);

  const defaultCreation = useCallback(() => {
    const newSteps = [];

    if (aiChatWrapRef) {
      const paddings = 12;

      const popoverPaddingTop = 23;
      const popoverTop = (aiChatWrapRef.offsetTop - paddings) + (aiChatWrapRef.offsetHeight + (2 * paddings) + popoverPaddingTop);

      const homeAiChatWrapperSteps = [
        {
          mask: {
            $left: aiChatWrapRef ? aiChatWrapRef.offsetLeft - paddings : 0,
            $top: aiChatWrapRef ? aiChatWrapRef.offsetTop - paddings : 0,
            $width: aiChatWrapRef ? aiChatWrapRef.offsetWidth + (2 * paddings) : 0,
            $height: aiChatWrapRef ? aiChatWrapRef.offsetHeight + (2 * paddings) : 0,
            $customRadius: 24,
            $zIndex: 99999,
          },
          popover: {
            $top: popoverTop,
            $left: isMobile ? 0 : '50%',
            $padding: '0',
            $boxSizing: 'content-box',
            $transform: isMobile ? 'unset' : 'translate(-50%, 0%)',
            $width: isMobile ? (windowRect.width - 20) : 480,
            $height: 378,
            $customRadius: 16,
            $zIndex: 999999,
            content: PopoverContent(),
            pointer: isMobile ? 'topLeft' : 'topMiddle',
            $maxwidth: 480,
          },
        },
      ];
      newSteps.push(...homeAiChatWrapperSteps);
    }

    return newSteps;
  }, [
    aiChatWrapRef,
    isMobile,
    PopoverContent,
    windowRect.width,
  ]);

  const forceMountSteps = useCallback(() => {
    setUsedRef(aiChatWrapRef);
    const newSteps = defaultCreation();

    setSteps(newSteps);
  }, [
    aiChatWrapRef,
    defaultCreation,
  ]);

  const mountSteps = useCallback(() => {
    if (hasViewAlready) {
      return;
    }

    if (usedRef !== aiChatWrapRef) {
      setUsedRef(aiChatWrapRef);

      const newSteps = defaultCreation();

      if (newSteps !== steps) {
        setSteps(newSteps);
      }
    }
  }, [
    aiChatWrapRef,
    steps,
    usedRef,
    defaultCreation,
    hasViewAlready,
  ]);

  useEffect(() => {
    mountSteps();
  }, [mountSteps]);

  useEffect(() => {
    if (renderAiChatGuide) {
      forceMountSteps();
    }
  }, [renderAiChatGuide, forceMountSteps, dispatch]);

  if (steps.length === 0) {
    return <></>;
  }

  return (
    <>
      <Guide
        steps={steps}
        testId="guide"
        ignoreClickOutside
        onClose={handleClose}
        $pointerWidth={16}
      />
    </>
  );
};

export default AiGuides;
